/**
 * Create a tender from Word: the headline details, the tender pack, then
 * io.ai takes over in the conversation (summary, research, questions,
 * outline, plans), exactly as the web creator does. Two short steps on one
 * screen; the document is linked to the new bid the moment setup starts,
 * so closing Word and coming back lands in the same conversation.
 */
import { ArrowLeftIcon, FileIcon, UploadIcon, XIcon } from "@/design/icons";
import { type FC, type ReactNode, useRef, useState } from "react";

import { Button } from "@/design/ui/button";
import { Input } from "@/design/ui/input";

import {
  ACCEPTED_TENDER_FILES,
  createBid,
  type PendingCreation,
  startSetup,
  type TenderDetailsInput,
  uploadTenderLibraryFiles,
  type UploadProgress,
  writePendingCreation,
} from "../tender/createTender";

export type CreateTenderScreenProps = {
  token: string;
  documentId: string | null;
  email: string | null;
  /** The document's title, offered as the tender name. */
  suggestedTitle: string | null;
  /** A creation that made a bid earlier but never started its session. */
  pending: PendingCreation | null;
  onBack: () => void;
  /** Setup has started: link the document to the bid; the pane opens the creator thread once it exists. */
  onStarted: (bidId: string) => void;
};

type Phase = "form" | "creating" | "uploading" | "starting";

export const CreateTenderScreen: FC<CreateTenderScreenProps> = ({
  token,
  documentId,
  email,
  suggestedTitle,
  pending,
  onBack,
  onStarted,
}) => {
  const [details, setDetails] = useState<TenderDetailsInput>({
    title: pending?.title ?? suggestedTitle ?? "",
    client: "",
    deadline: "",
    value: "",
  });
  const [files, setFiles] = useState<File[]>([]);
  const [phase, setPhase] = useState<Phase>("form");
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const addFiles = (incoming: FileList | File[] | null) => {
    if (!incoming) return;
    const next = [...files];
    for (const file of Array.from(incoming)) {
      if (!next.some((f) => f.name === file.name && f.size === file.size)) next.push(file);
    }
    setFiles(next);
  };
  const canStart = details.title.trim().length > 1 && files.length > 0 && phase === "form";

  const start = async () => {
    setError(null);
    try {
      let bidId = pending?.bidId ?? null;
      if (!bidId) {
        setPhase("creating");
        bidId = await createBid(details, token);
        writePendingCreation(documentId, { bidId, title: details.title.trim() });
      }
      setPhase("uploading");
      const { failed } = await uploadTenderLibraryFiles(bidId, files, token, setProgress);
      if (failed.length === files.length) {
        throw new Error(`None of the documents could be uploaded (${failed[0].reason}).`);
      }
      setPhase("starting");
      const failedNames = new Set(failed.map((f) => f.name));
      await startSetup({
        token,
        bidId,
        fileNames: files.map((f) => f.name).filter((name) => !failedNames.has(name)),
        scope: { documentId, ownerEmail: email },
      });
      writePendingCreation(documentId, null);
      onStarted(bidId);
    } catch (err) {
      setPhase("form");
      setError(err instanceof Error ? err.message : "Could not start the tender setup");
    }
  };

  const busy = phase !== "form";

  return (
    <div className="flex h-full flex-col bg-surface">
      <div className="flex h-11 shrink-0 items-center gap-1 border-b border-line px-2">
        <button
          type="button"
          aria-label="Back"
          onClick={onBack}
          disabled={busy}
          className="flex size-7 items-center justify-center rounded-sm text-fg-2 hover:bg-subtle hover:text-fg disabled:opacity-40"
        >
          <ArrowLeftIcon className="size-4" />
        </button>
        <span className="text-sm font-medium text-fg-3">New tender</span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pb-4">
        <div className="px-4 pb-2 pt-4">
          <h1 className="text-lg font-semibold leading-snug">Create a tender</h1>
          <p className="mt-0.5 text-sm text-fg-3">
            Give it a name and drop the tender pack. io.ai reads it, researches the buyer, asks a few questions and
            builds the outline here in the chat.
          </p>
        </div>

        {pending && (
          <div className="mx-3 mb-1 rounded-sm border border-line bg-subtle px-2.5 py-2 text-xs text-fg-2">
            A tender called <span className="font-semibold text-fg">{pending.title}</span> was created earlier but its
            setup never started. Add the documents to continue with it.
          </div>
        )}

        <div className="flex flex-col gap-3 px-4 pt-2">
          <Field label="Tender name" required>
            <Input
              value={details.title}
              onChange={(e) => setDetails({ ...details, title: e.target.value })}
              placeholder="e.g. Facilities management, Leeds City Council"
              disabled={busy || Boolean(pending)}
              autoFocus
            />
          </Field>
          <Field label="Client">
            <Input
              value={details.client}
              onChange={(e) => setDetails({ ...details, client: e.target.value })}
              placeholder="The buying organisation"
              disabled={busy || Boolean(pending)}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Submission date">
              <Input
                type="date"
                value={details.deadline}
                onChange={(e) => setDetails({ ...details, deadline: e.target.value })}
                disabled={busy || Boolean(pending)}
              />
            </Field>
            <Field label="Value (£)">
              <Input
                inputMode="numeric"
                value={details.value}
                onChange={(e) => setDetails({ ...details, value: e.target.value.replace(/[^\d]/g, "") })}
                placeholder="Optional"
                disabled={busy || Boolean(pending)}
              />
            </Field>
          </div>
          <p className="-mt-1 text-xs text-fg-4">Anything you leave blank, io.ai reads from the documents or asks.</p>
        </div>

        <div className="px-4 pt-4">
          <div className="mb-1 text-xs font-semibold text-fg-3">Tender pack</div>
          <input
            ref={fileInput}
            type="file"
            multiple
            accept={ACCEPTED_TENDER_FILES}
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => fileInput.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              addFiles(e.dataTransfer.files);
            }}
            className={
              "flex w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed px-3 py-5 text-center transition-colors duration-fast disabled:opacity-60 " +
              (dragging ? "border-accent bg-accent-soft" : "border-line-strong bg-subtle hover:bg-hover")
            }
          >
            <UploadIcon className="size-4 text-fg-3" />
            <span className="text-sm font-semibold">Drop the tender documents here</span>
            <span className="text-xs text-fg-3">
              ITT, specification, questions, pricing schedule · PDF, Word, Excel
            </span>
          </button>
          {files.length > 0 && (
            <ul className="mt-2 divide-y divide-line/70 rounded-md border border-line">
              {files.map((file) => (
                <li key={`${file.name}:${file.size}`} className="flex items-center gap-2 px-2.5 py-1.5 text-sm">
                  <FileIcon className="size-3.5 shrink-0 text-fg-3" />
                  <span className="min-w-0 flex-1 truncate">{file.name}</span>
                  <span className="shrink-0 text-xs text-fg-4">{Math.max(1, Math.round(file.size / 1024))} KB</span>
                  {!busy && (
                    <button
                      type="button"
                      aria-label={`Remove ${file.name}`}
                      onClick={() => setFiles(files.filter((f) => f !== file))}
                      className="flex size-5 items-center justify-center rounded-sm text-fg-3 hover:bg-hover hover:text-fg"
                    >
                      <XIcon className="size-3" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {error && (
          <div className="mx-3 mt-3 rounded-sm border border-danger/30 bg-danger-soft px-2.5 py-2 text-xs text-danger">
            {error}
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-line p-3">
        <Button className="w-full" disabled={!canStart} onClick={() => void start()}>
          {phase === "form" && "Start setup"}
          {phase === "creating" && "Creating the tender…"}
          {phase === "uploading" &&
            (progress
              ? `Uploading ${Math.min(progress.done + 1, progress.total)} of ${progress.total}…`
              : "Uploading…")}
          {phase === "starting" && "Starting io.ai…"}
        </Button>
        <p className="mt-1.5 text-center text-xs text-fg-4">
          You can close Word during setup; io.ai keeps going and the chat is here when you return.
        </p>
      </div>
    </div>
  );
};

const Field: FC<{ label: string; required?: boolean; children: ReactNode }> = ({ label, required, children }) => (
  <label className="block">
    <span className="mb-1 block text-xs font-semibold text-fg-3">
      {label}
      {required && <span className="text-accent"> *</span>}
    </span>
    {children}
  </label>
);
